class InterceptorManager {
    constructor() {
        this.handlers = [];
    }

    use(onFulfilled, onRejected) {
        this.handlers.push({onFulfilled, onRejected});
        return this.handlers.length - 1;
    }
}

function isFormData(value) {
    return typeof FormData !== 'undefined' && value instanceof FormData;
}

function csrfToken() {
    if (typeof document === 'undefined') return null;

    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.getAttribute('content') : null;
}

function appendParam(params, key, value) {
    if (value === null || value === undefined) return;

    if (Array.isArray(value)) {
        value.forEach((item) => appendParam(params, `${key}[]`, item));
    } else if (typeof value === 'object') {
        params.append(key, JSON.stringify(value));
    } else {
        params.append(key, value);
    }
}

function buildUrl(url, params) {
    if (!params) return url;

    const searchParams = new URLSearchParams();
    Object.keys(params).forEach((key) => appendParam(searchParams, key, params[key]));

    const queryString = searchParams.toString();
    if (!queryString) return url;

    return `${url}${url.includes('?') ? '&' : '?'}${queryString}`;
}

function normalizeHeaders(headers) {
    const normalized = {};
    Object.keys(headers || {}).forEach((key) => {
        if (headers[key] !== null && headers[key] !== undefined) {
            normalized[key] = headers[key];
        }
    });
    return normalized;
}

function parseHeaders(headers) {
    const parsed = {};
    headers.forEach((value, key) => {
        parsed[key] = value;
    });
    return parsed;
}

function parseResponseBody(text, contentType) {
    if (!text) return null;

    if ((contentType || '').includes('application/json') || /^[\[{]/.test(text.trim())) {
        try {
            return JSON.parse(text);
        } catch (error) {
            return text;
        }
    }

    return text;
}

async function parseResponseData(response, config) {
    if (config.responseType === 'blob') return response.blob();
    if (config.responseType === 'arraybuffer') return response.arrayBuffer();
    if (config.responseType === 'text') return response.text();

    const text = await response.text();
    return parseResponseBody(text, response.headers.get('content-type'));
}

function applyTransformResponse(data, transformResponse) {
    if (!transformResponse) return data;

    // This supports the dormant store-generator hook. Unlike Axios, the data has
    // already been parsed before transforms run because no active store relies on
    // raw-string transform semantics.
    const transforms = Array.isArray(transformResponse) ? transformResponse : [transformResponse];
    return transforms.reduce((currentData, transform) => {
        return typeof transform === 'function' ? transform(currentData) : currentData;
    }, data);
}

class FetchHttpClient {
    constructor() {
        this.interceptors = {
            request: new InterceptorManager(),
            response: new InterceptorManager()
        };
    }

    get(url, config = {}) {
        return this.request({...config, method: 'GET', url});
    }

    delete(url, config = {}) {
        return this.request({...config, method: 'DELETE', url});
    }

    post(url, data = undefined, config = {}) {
        return this.request({...config, method: 'POST', url, data});
    }

    put(url, data = undefined, config = {}) {
        return this.request({...config, method: 'PUT', url, data});
    }

    patch(url, data = undefined, config = {}) {
        return this.request({...config, method: 'PATCH', url, data});
    }

    async request(config) {
        let requestConfig = {...config};

        try {
            for (const interceptor of this.interceptors.request.handlers) {
                if (interceptor.onFulfilled) {
                    requestConfig = await interceptor.onFulfilled(requestConfig);
                }
            }

            const response = await this.performRequest(requestConfig);

            let interceptedResponse = response;
            for (const interceptor of this.interceptors.response.handlers) {
                if (interceptor.onFulfilled) {
                    interceptedResponse = await interceptor.onFulfilled(interceptedResponse);
                }
            }
            return interceptedResponse;
        } catch (error) {
            let interceptedError = error;
            for (const interceptor of this.interceptors.response.handlers) {
                if (interceptor.onRejected) {
                    try {
                        return await interceptor.onRejected(interceptedError);
                    } catch (nextError) {
                        interceptedError = nextError;
                    }
                }
            }
            throw interceptedError;
        }
    }

    async performRequest(config) {
        const method = (config.method || 'GET').toUpperCase();
        const headers = normalizeHeaders(config.headers || {});
        const data = config.data;

        if (!headers.Accept && !headers.accept) {
            headers.Accept = 'application/json, text/plain, */*';
        }

        const token = csrfToken();
        if (token && !['GET', 'HEAD', 'OPTIONS', 'TRACE'].includes(method)) {
            headers['X-CSRF-Token'] = token;
        }

        const fetchOptions = {
            method,
            credentials: 'same-origin',
            headers
        };

        if (data !== undefined && data !== null) {
            if (isFormData(data)) {
                delete headers['Content-Type'];
                delete headers['content-type'];
                fetchOptions.body = data;
            } else if (typeof data === 'string') {
                fetchOptions.body = data;
            } else {
                if (!headers['Content-Type'] && !headers['content-type']) {
                    headers['Content-Type'] = 'application/json';
                }
                fetchOptions.body = JSON.stringify(data);
            }
        }

        const response = await fetch(buildUrl(config.url, config.params), fetchOptions);
        const dataPayload = applyTransformResponse(
            await parseResponseData(response, config),
            config.transformResponse
        );

        const axiosResponse = {
            data: dataPayload,
            status: response.status,
            statusText: response.statusText,
            headers: parseHeaders(response.headers),
            config
        };

        if (response.ok) return axiosResponse;

        const error = new Error(response.statusText || `Request failed with status code ${response.status}`);
        error.response = axiosResponse;
        error.config = config;
        throw error;
    }
}

export default function createHttpClient() {
    return new FetchHttpClient();
}
